import React, { JSX } from "react";
import { Delete } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Typography } from "@mui/material";

import Panel from "../../../../../components/Panel";
import { tl } from "../../../../../components/translate";
import { rupeeDisplay } from "../../../../../helpers/rupee";
import EmployeeShow from "../../../../Employee/EmployeeShow";
import { ICtc } from "../../../../../interfaces/CtcInterface";
import { rupeesDisplayFull } from "../../../../../helpers/util";
import { useDeleteCtc } from "../../../../../hooks/query/useCtcs";
import OkhatiDialog from "../../../../../components/Dialog/Dialog";
import { RetirementFundType } from "../../../../../constants/payroll";
import ServiceproviderShow from "../../../../ServiceProvider/ServiceproviderShow";
import { bsFullDate } from "../../../../../components/Calendar/functions/calendarFunctions";

interface PropTypes {
  onClose: () => void;
  ctc: ICtc | null;
  onDeleteSuccess: (data: ICtc) => void;
  onEditOpen: () => void;
}

// eslint-disable-next-line import/prefer-default-export
export function CtcInfoPanel({
  onClose,
  ctc,
  onDeleteSuccess,
  onEditOpen
}: PropTypes): JSX.Element {
  const { mutate: deleteCtcMutation } = useDeleteCtc();

  const [confirmEdit, setConfirmEdit] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [showEmployeeInfo, setShowEmployeeInfo] = React.useState(false);

  const handleDelete = () => {
    if (!ctc?.id) return;
    deleteCtcMutation(ctc.id, {
      onSuccess: (response) => {
        if (response.success) {
          onDeleteSuccess(ctc);
          onClose();
        }
      },
      onSettled: () => {
        setConfirmDelete(false);
      }
    });
  };

  return (
    <Panel
      onClose={() => onClose()}
      titleJsx={
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6" noWrap>
              {tl("ctc.info")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", ml: "8px" }}>
            <IconButton
              sx={{ cursor: "pointer", p: "5px" }}
              onClick={() => onEditOpen()}
              data-testmation="editCtcButton"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              sx={{ cursor: "pointer", p: "5px" }}
              onClick={() => setConfirmDelete(true)}
              data-testmation="deleteCtcButton"
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>
      }
    >
      {confirmDelete && (
        <OkhatiDialog
          title={tl("ctc.delete")}
          description="Are you sure you want to delete this ctc? This action cannot be undone."
          next={handleDelete}
          cancel={() => setConfirmDelete(false)}
          readMode={false}
        />
      )}

      {confirmEdit && (
        <OkhatiDialog
          title={tl("ctc.delete")}
          description="Are you sure you want to edit this ctc? Please review the change carefully."
          next={handleDelete}
          cancel={() => setConfirmEdit(false)}
          readMode={false}
        />
      )}

      {showEmployeeInfo && ctc?.employeeId && (
        <EmployeeShow
          isEmployeeOnlyTab={false}
          employeeId={ctc.employeeId}
          handleClose={() => {
            setShowEmployeeInfo(false);
          }}
        />
      )}

      {showEmployeeInfo && ctc?.serviceProviderId && (
        <ServiceproviderShow
          serviceProviderId={ctc.serviceProviderId}
          handleViewClose={() => {
            setShowEmployeeInfo(false);
          }}
        />
      )}

      <Box>
        {/* basic informations */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
            {tl("payroll.basicInformation")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography width="40%">{tl("name")}</Typography>
            <Typography
              sx={{ cursor: "pointer", textDecoration: "underline", textTransform: "capitalize" }}
              onClick={() => {
                setShowEmployeeInfo(true);
              }}
            >
              {(() => {
                if (ctc?.employee) {
                  return `${ctc.employee.firstName} ${ctc.employee.lastName}`;
                }
                if (ctc?.serviceProvider) {
                  return `${ctc.serviceProvider.firstName} ${ctc.serviceProvider.lastName}`;
                }
                return "-";
              })()}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography width="40%">{tl("payroll.employeeType")}</Typography>
            <Typography>{ctc?.employeeId ? "Employee" : "Service Provider"}</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography width="40%">{tl("ctc.effectiveFrom")}</Typography>
            <Typography>{ctc?.effectiveFrom ? bsFullDate(ctc.effectiveFrom) : "-"}</Typography>
          </Box>
        </Box>

        {/* monthly earnings */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
            {tl("payroll.monthlyEarnings")}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography width="40%">{tl("payroll.basicSalary")}</Typography>
            <Typography>{rupeesDisplayFull(ctc?.monthlyEarnings?.basicSalary || 0)}</Typography>
          </Box>

          {ctc?.monthlyEarnings &&
            Object.entries(ctc.monthlyEarnings?.allowances)
              .filter(([, val]) => val?.value)
              .map(([field, val]) => (
                <Box key={field} sx={{ display: "flex", alignItems: "center" }}>
                  <Typography width="40%">{tl(`payroll.${field}`)}</Typography>
                  <Typography>
                    {typeof val.value === "number" ? rupeeDisplay(val.value) : "-"}
                  </Typography>
                </Box>
              ))}
        </Box>

        {/* yearly earnings */}
        {ctc?.yearlyEarnings && Object.keys(ctc.yearlyEarnings).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
              {tl("payroll.yearlyEarnings")}
            </Typography>
            {Object.entries(ctc.yearlyEarnings)
              .filter(([, val]) => val?.value)
              .map(([field, val]) => (
                <Box key={field} sx={{ display: "flex", alignItems: "center" }}>
                  <Typography width="40%">{tl(`payroll.${field}`)}</Typography>
                  <Typography>{rupeeDisplay(val.value)}</Typography>
                </Box>
              ))}
          </Box>
        )}

        {/* ssf contributions */}
        {ctc?.retirementFundType === RetirementFundType.SOCIAL_SECURITY_FUND && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
              {tl("payroll.ssfContributions")}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography width="40%">{tl("payroll.employeeSsfContribution")}</Typography>
              <Typography>
                {ctc?.retirementFundContributionSummary?.ssfContributions?.employee
                  ? rupeeDisplay(ctc.retirementFundContributionSummary?.ssfContributions?.employee)
                  : "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography width="40%">{tl("payroll.employerSsfContribution")}</Typography>
              <Typography>
                {ctc?.retirementFundContributionSummary?.ssfContributions?.employer
                  ? rupeeDisplay(ctc.retirementFundContributionSummary?.ssfContributions?.employer)
                  : "-"}
              </Typography>
            </Box>
          </Box>
        )}

        {/* pf contributions */}
        {ctc?.retirementFundType === RetirementFundType.PROVIDENT_FUND && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "bolder", mb: "4px" }}>
              {tl("payroll.pfContributions")}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography width="40%">{tl("payroll.employeePfContribution")}</Typography>
              <Typography>
                {ctc?.retirementFundContributionSummary?.pfContributions?.employee
                  ? rupeeDisplay(ctc.retirementFundContributionSummary?.pfContributions?.employee)
                  : "-"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography width="40%">{tl("payroll.employerPfContribution")}</Typography>
              <Typography>
                {ctc?.retirementFundContributionSummary?.pfContributions?.employer
                  ? rupeeDisplay(ctc?.retirementFundContributionSummary?.pfContributions?.employer)
                  : "-"}
              </Typography>
            </Box>
          </Box>
        )}

        {/* remark */}
        {ctc?.remarks && (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography width="40%" fontWeight={600}>
              {tl("remarks")}
            </Typography>
            <Typography>{ctc?.remarks || "-"}</Typography>
          </Box>
        )}
      </Box>
    </Panel>
  );
}
